import React, {useCallback, useEffect, useMemo, useState} from "react";
import { useRef } from "react";
import Modal from "../comman/modal.js";
import Button from "../comman/button.js";
import {
    deleteDishAddition,
    fetchAdditionGroups,
    fetchDishAdditions,
    saveDishAddition,
    updateDishAddition
} from "../../services/menu-service.js";
import ConfirmationModal from "./ConfirmationModal.jsx";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import LoadingSpinner from "../LoadingSpinner/loadingSpinner.js";
import { translateToRuAndEn } from "../../utils/googleTranslate.js";

const DishAdditionsModal = ({closeModal, toastRef}) => {
    const [additions, setAdditions] = useState([]);
    const [originalAdditions, setOriginalAdditions] = useState([]);
    const [additionGroups, setAdditionGroups] = useState([]);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [selectedAddition, setSelectedAddition] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isTranslating, setIsTranslating] = useState({});
    const scrollContainerRef = useRef(null);
    const isFetching = useRef(false);
    const { language } = useLanguage();

    const fetchAdditionsAndGroups = useCallback(async () => {
        if (isFetching.current) return;
        isFetching.current = true;
        setLoading(true);
        try {
            const [additionsData, groupsData] = await Promise.all([
                fetchDishAdditions(),
                fetchAdditionGroups()
            ]);
            setAdditions(additionsData);
            setOriginalAdditions(additionsData);
            setAdditionGroups(groupsData);
        } catch (error) {
            console.error("Error fetching additions and groups:", error);
        } finally {
            setLoading(false);
            isFetching.current = false;
        }
    }, []);

    useEffect(() => {
        fetchAdditionsAndGroups();
    }, [fetchAdditionsAndGroups]);

    const handleInputChange = (id, field, value) => {
        setAdditions((prevAdditions) =>
            prevAdditions.map((addition) => {
                if (addition.id === id) {
                    if (field === 'group') {
                        const selectedGroup = additionGroups.find(g => g.id === Number(value)) || null;
                        return { ...addition, group: selectedGroup };
                    }
                    return { ...addition, [field]: value };
                }
                return addition;
            })
        );
    };

    const isEdited = (addition) => {
        const originalAddition = originalAdditions.find((item) => item.id === addition.id);
        if (!originalAddition) return true;
        return (
            originalAddition.nameHy !== addition.nameHy ||
            (originalAddition.nameRu || '') !== (addition.nameRu || '') ||
            (originalAddition.nameEn || '') !== (addition.nameEn || '') ||
            Number(originalAddition.price) !== Number(addition.price) ||
            originalAddition.group?.id !== addition.group?.id
        );
    };

    const handleTranslateAddition = async (additionId) => {
        const addition = additions.find(a => a.id === additionId);
        if (!addition || !addition.nameHy || !addition.nameHy.trim()) {
            toastRef.current?.addToast(
                getTranslation('toast.enterArmenianFirst', language) || "Please enter Armenian name first",
                "warning"
            );
            return;
        }

        setIsTranslating(prev => ({ ...prev, [additionId]: true }));
        try {
            const translations = await translateToRuAndEn(addition.nameHy);
            handleInputChange(additionId, "nameRu", translations.ru);
            handleInputChange(additionId, "nameEn", translations.en);
            toastRef.current?.addToast(
                getTranslation('toast.translationSuccess', language) || "Translation completed successfully!",
                "success"
            );
        } catch (error) {
            console.error("Translation error:", error);
            toastRef.current?.addToast(
                getTranslation('toast.translationFailed', language) || "Failed to translate. Please try again.",
                "error"
            );
        } finally {
            setIsTranslating(prev => ({ ...prev, [additionId]: false }));
        }
    };

    const handleDelete = (id, isNewAddition = false) => {
        setIsConfirmModalOpen(false);
        setSelectedAddition(null);

        if (isNewAddition) {
            setAdditions(prevAdditions => prevAdditions.filter(addition => addition.id !== id));
            return;
        }

        deleteDishAddition(id).then(() => {
            fetchAdditionsAndGroups();
        });
    };

    const handleSave = async (addition) => {
        try {
            // Create a new addition object
            const newAddition = {
                nameHy: addition.nameHy,
                price: parseFloat(addition.price),
                group: addition.group || null
            };

            await saveDishAddition(newAddition);
        } catch (error) {
            console.error("Error saving addition:", error);
        }
    };

    const handleUpdate = async (addition) => {
        try {
            const originalAddition = originalAdditions.find(orig => orig.id === addition.id);
            if (!originalAddition) {
                console.error(`Cannot find original addition with ID ${addition.id}`);
                return;
            }

            // Check if anything actually changed
            const nameChanged = originalAddition.nameHy !== addition.nameHy;
            const nameRuChanged = (originalAddition.nameRu || '') !== (addition.nameRu || '');
            const nameEnChanged = (originalAddition.nameEn || '') !== (addition.nameEn || '');
            const priceChanged = originalAddition.price !== parseFloat(addition.price);
            const groupChanged = (originalAddition.group?.id || null) !== (addition.group?.id || null);

            if (!nameChanged && !nameRuChanged && !nameEnChanged && !priceChanged && !groupChanged) {
                return;
            }

            // Create an updated addition object
            const updatedAddition = {
                id: addition.id,
                nameHy: addition.nameHy,
                price: parseFloat(addition.price),
                group: addition.group || null
            };

            await updateDishAddition(updatedAddition);
        } catch (error) {
            console.error("Error updating addition:", error);
        }
    };

    const openConfirmationModal = (addition) => {
        setSelectedAddition(addition);
        setIsConfirmModalOpen(true);
    };

    const closeConfirmationModal = () => {
        setSelectedAddition(null);
        setIsConfirmModalOpen(false);
    };

    const handleAdd = () => {
        setAdditions((prevAdditions) => [
            ...prevAdditions,
            { id: `new-${Date.now()}-${Math.random()}`, nameHy: "", price: 0, nameEn: "", nameRu: "", group: null, isNew: true }
        ]);

        requestAnimationFrame(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTo({
                    top: scrollContainerRef.current.scrollHeight,
                    behavior: 'smooth'
                });
            }
        });
    };

    const handleConfirm = () => {
        const promises = additions
            .filter(addition => addition.nameHy && (addition.price || addition.price === 0))
            .map(addition => {
                const updatedAddition = {
                    ...addition,
                    price: parseFloat(addition.price)
                };

                if (addition.isNew) {
                    return handleSave(updatedAddition);
                } else if (isEdited(addition)) {
                    return handleUpdate(updatedAddition);
                }
                return null;
            })
            .filter(promise => promise !== null);

        Promise.all(promises)
            .then(() => {
                return fetchAdditionsAndGroups();
            })
            .then(() => {
                if (toastRef?.current) {
                    toastRef.current.addToast(
                        getTranslation('toast.statusUpdated', language) || "Additions saved successfully",
                        "success"
                    );
                }
                closeModal();
            })
            .catch(error => {
                console.error("Error during confirm operation:", error);
                if (toastRef?.current) {
                    toastRef.current.addToast(
                        getTranslation('toast.updateFailed', language) || "Failed to save additions",
                        "error"
                    );
                }
                fetchAdditionsAndGroups();
            });
    };

    const groupedAdditions = useMemo(() => {
        const groups = additions.reduce((acc, addition) => {
            const groupId = addition.group?.id ?? 'ungrouped';
            if (!acc[groupId]) {
                acc[groupId] = {
                    groupInfo: addition.group,
                    items: []
                };
            }
            acc[groupId].items.push(addition);
            return acc;
        }, {});

        const ungrouped = groups['ungrouped'];
        delete groups['ungrouped'];

        const sortedGroupKeys = Object.keys(groups).sort((a, b) => {
             const groupA = groups[a].groupInfo?.nameHy || '';
             const groupB = groups[b].groupInfo?.nameHy || '';
             return groupA.localeCompare(groupB);
        });

        const sortedGroups = sortedGroupKeys.map(key => groups[key]);

        if (ungrouped) {
            sortedGroups.push({ ...ungrouped, groupInfo: { nameHy: "Չխմբավորված" } });
        }

        return sortedGroups;
    }, [additions]);

    return (
        <>
        <Modal
            isOpen={true}
            onClose={closeModal}
            title={getTranslation('menu.additions', language) || 'Հավելումներ'}
            size="xl"
        >
            {loading ? (
                <div className="flex justify-center items-center py-12">
                    <LoadingSpinner />
                </div>
            ) : (
                <>
                    <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2" ref={scrollContainerRef}>
                        {groupedAdditions.map(({ groupInfo, items }, groupIndex) => (
                            <div key={groupInfo?.id || 'ungrouped'}>
                                 <h3 className="text-lg font-semibold mb-3 sticky top-0 bg-white dark:bg-[#161b22] py-1 text-gray-900 dark:text-[#c9d1d9]">
                                    {groupInfo?.nameHy || "Չխմբավորված"}
                                </h3>
                                <div className="space-y-4">
                                    {items.map((addition) => (
                                        <div
                                            key={addition.id}
                                            className="space-y-2 mb-4 p-3 border border-gray-200 dark:border-[#30363d] rounded-lg"
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <select
                                                     id={`addition-group-${addition.id}`}
                                                     name={`addition-group-${addition.id}`}
                                                     value={addition.group?.id || ""}
                                                     onChange={(e) => handleInputChange(addition.id, "group", e.target.value)}
                                                     aria-label="Addition group"
                                                     autoComplete="off"
                                                     className="w-40 px-3 py-2 border border-gray-300 dark:border-[#30363d] rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 dark:focus:ring-[#30363d] bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9]"
                                                 >
                                                     <option value="">Խումբ չկա</option>
                                                     {additionGroups.map(group => (
                                                         <option key={group.id} value={group.id}>
                                                             {group.nameHy}
                                                         </option>
                                                     ))}
                                                 </select>
                                                <input
                                                    id={`addition-name-${addition.id}`}
                                                    name={`addition-name-${addition.id}`}
                                                    type="text"
                                                    value={addition.nameHy}
                                                    onChange={(e) => {
                                                        if (e.target.value.length <= 20) {
                                                            handleInputChange(addition.id, "nameHy", e.target.value);
                                                        }
                                                    }}
                                                    placeholder="Հավելում (Հայերեն)"
                                                    aria-label="Addition name Armenian"
                                                    autoComplete="off"
                                                    className="flex-grow px-3 py-2 border border-gray-300 dark:border-[#30363d] rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 dark:focus:ring-[#30363d] bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9] placeholder:text-gray-400 dark:placeholder:text-[#6e7681]"
                                                />
                                                <input
                                                    id={`addition-price-${addition.id}`}
                                                    name={`addition-price-${addition.id}`}
                                                    type="number"
                                                    value={addition.price}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        // Prevent negative values
                                                        if (value !== '' && Number(value) < 0) {
                                                            return;
                                                        }
                                                        if (value.length <= 10) {
                                                            handleInputChange(addition.id, "price", value);
                                                        }
                                                    }}
                                                    min="0"
                                                    placeholder="Գին"
                                                    aria-label="Addition price"
                                                    autoComplete="off"
                                                    className="w-24 px-3 py-2 border border-gray-300 dark:border-[#30363d] rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 dark:focus:ring-[#30363d] bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9] placeholder:text-gray-400 dark:placeholder:text-[#6e7681]"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => openConfirmationModal(addition)}
                                                    aria-label="Delete addition"
                                                    className="text-gray-400 hover:text-red-500 ml-1 p-1"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                    </svg>
                                                </button>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    id={`addition-name-ru-${addition.id}`}
                                                    name={`addition-name-ru-${addition.id}`}
                                                    type="text"
                                                    value={addition.nameRu || ''}
                                                    onChange={(e) => {
                                                        if (e.target.value.length <= 20) {
                                                            handleInputChange(addition.id, "nameRu", e.target.value);
                                                        }
                                                    }}
                                                    placeholder="Հավելում (Ռուսերեն)"
                                                    aria-label="Addition name Russian"
                                                    autoComplete="off"
                                                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-[#30363d] rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 dark:focus:ring-[#30363d] bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9] placeholder:text-gray-400 dark:placeholder:text-[#6e7681]"
                                                />
                                                <input
                                                    id={`addition-name-en-${addition.id}`}
                                                    name={`addition-name-en-${addition.id}`}
                                                    type="text"
                                                    value={addition.nameEn || ''}
                                                    onChange={(e) => {
                                                        if (e.target.value.length <= 20) {
                                                            handleInputChange(addition.id, "nameEn", e.target.value);
                                                        }
                                                    }}
                                                    placeholder="Հավելում (Անգլերեն)"
                                                    aria-label="Addition name English"
                                                    autoComplete="off"
                                                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-[#30363d] rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 dark:focus:ring-[#30363d] bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9] placeholder:text-gray-400 dark:placeholder:text-[#6e7681]"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleTranslateAddition(addition.id)}
                                                    disabled={isTranslating[addition.id] || !addition.nameHy || !addition.nameHy.trim()}
                                                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition-colors duration-200 flex items-center gap-1"
                                                    title={getTranslation('menu.translate', language) || 'Translate'}
                                                >
                                                    {isTranslating[addition.id] ? (
                                                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                    ) : (
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                                        </svg>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                 </div>
                                 {groupIndex < groupedAdditions.length - 1 && <hr className="my-4 border-gray-200 dark:border-[#30363d]"/>}
                            </div>
                        ))}

                        {additions.length === 0 && handleAdd()}
                    </div>

                    <div className="flex items-center justify-between mt-6 gap-3 border-t border-gray-200 dark:border-[#30363d] pt-4">
                        <Button
                            variant="secondary"
                            size="lg"
                            onClick={handleAdd}
                            className="flex-1"
                        >
                            <span className="mr-1">+</span> {getTranslation('menu.addAddition', language) || 'Ավելացնել հավելում'}
                        </Button>

                        <Button
                            variant="primary"
                            size="lg"
                            onClick={handleConfirm}
                            className="flex-1"
                        >
                            {getTranslation('common.confirm', language) || 'Հաստատել'}
                        </Button>
                    </div>
                </>
            )}

            {isConfirmModalOpen && selectedAddition && (
                <ConfirmationModal
                    description={
                        <>
                            {selectedAddition?.relevantDishes?.length > 0 ? (
                                <>
                                    <p className="mb-4">
                                        Ջնջելով նշված հավելումը ({selectedAddition.nameHy}) այն նաև կհեռացվի նշված
                                        ճաշատեսակներից:
                                    </p>
                                    {selectedAddition.relevantDishes.map((item, index) => (
                                        <p key={index}>
                                            -{" "}
                                            <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={`https://www.bychef.am/menu/${item.dishId}`}
                                                className="text-blue-600 hover:underline"
                                            >
                                                {item.dishNameAm}
                                            </a>
                                        </p>
                                    ))}
                                </>
                            ) : (
                                <p>Ջնջել "{selectedAddition.nameHy || 'անանուն'}" հավելումը?</p>
                            )}
                        </>
                    }
                    onCancel={closeConfirmationModal}
                    onSubmit={() => handleDelete(selectedAddition.id, selectedAddition.isNew)}
                />
            )}
        </Modal>
    </>
    );
};

export default DishAdditionsModal;
